'use client'

import IconArrowRight from '@/components/icons/IconArrowRight'
import { cn } from '@/lib/utils'
import { urlForImage } from '@/sanity/lib/image'
import MuxVideo from '@/components/MuxVideo'
import { motion, useInView, useScroll, useTransform } from 'motion/react'
import Image from 'next/image'
import Link from 'next/link'
import { useEffect, useRef } from 'react'

interface NextProjectBannerProps {
	nextProject: any
}

export default function NextProjectBanner({ nextProject }: NextProjectBannerProps) {
	const ref = useRef(null)
	const { scrollYProgress } = useScroll({
		target: ref,
		offset: ['start center', 'start start']
	})
	const opacity = useTransform(scrollYProgress, [0, 1], [0.5, 1])

	const isInView = useInView(ref, {amount: 0})

	const preview = nextProject?.nextProjectPreview || nextProject?.preview

	return (
		<>
			<div ref={ref} className="sticky top-0 z-200 bg-white dark:bg-black pt-offset-header">
				<Link
					href={`/projects/${nextProject?.slug}`}
					className="flex-none flex items-center justify-between gap-x-4 px-site sm:pt-5 py-5 text-lg leading-120 text-black dark:text-white bg-[#f1f1f1] dark:bg-[#222]"
				>
					<span>
						{nextProject?.title}
					</span>
					<div className="flex items-center gap-3">
						<span className='hidden sm:block'>View next project</span>
						<span className="sm:hidden">Next</span>
						<IconArrowRight className="w-[0.825em] h-[0.825em]" />
					</div>
				</Link>
			</div>
			<Link href={`/projects/${nextProject?.slug}`} className="sticky bottom-0 bg-black block h-lvh">
				<motion.div style={{ opacity: opacity }}>
					{preview?.image && <Image
						className={cn(`absolute bottom-0 z-100 w-full h-lvh object-cover`, {
							'mobile:hidden': nextProject?.nextProjectPreviewMobile
						})}
						src={urlForImage(preview?.image).url()}
						sizes={`100vw`}
						width={preview?.image?.asset?.metadata?.dimensions?.width}
						height={preview?.image?.asset?.metadata?.dimensions?.height}
						alt={nextProject?.preview?.image?.alt || ''}
						placeholder="blur"
						blurDataURL={preview?.image?.asset?.metadata?.lqip}
						quality={90}
					/>}
					{preview?.video && isInView && <MuxVideo
						className={cn(`absolute bottom-0 z-100 w-full h-lvh object-cover`, {
							'mobile:hidden': nextProject?.nextProjectPreviewMobile
						})}
						playbackId={preview?.video?.asset?.playbackId}
						autoplay={true}
						loop={true}
						showAudioToggle={false}
					/>}
					{nextProject?.nextProjectPreviewMobile?.image && <Image
						className={cn(`absolute bottom-0 z-100 w-full h-lvh object-cover`, {
							'hidden mobile:block': nextProject?.nextProjectPreviewMobile
						})}
						src={urlForImage(nextProject?.nextProjectPreviewMobile?.image).url()}
						sizes={`100vw`}
						width={nextProject?.nextProjectPreviewMobile?.image?.asset?.metadata?.dimensions?.width}
						height={nextProject?.nextProjectPreviewMobile?.image?.asset?.metadata?.dimensions?.height}
						alt={nextProject?.nextProject?.nextProjectPreviewMobile?.image?.alt || ''}
						placeholder="blur"
						blurDataURL={nextProject?.nextProjectPreviewMobile?.image?.asset?.metadata?.lqip}
						quality={90}
					/>}
					{nextProject?.nextProjectPreviewMobile?.video && isInView && <MuxVideo
						className={cn(`absolute bottom-0 z-100 w-full h-lvh object-cover`, {
							'hidden mobile:block': nextProject?.nextProjectPreviewMobile
						})}
						playbackId={nextProject?.nextProjectPreviewMobile?.video?.asset?.playbackId}
						autoplay={true}
						loop={true}
						showAudioToggle={false}
					/>}
				</motion.div>
			</Link>
		</>
	)
}
